/**
 * Codecove "Timesheets for Jira" Forge app — no public submit API yet.
 * We surface a Submit button for the previous calendar month until the user
 * marks it submitted (after they click Submit in Jira when the period is Due).
 *
 * Deep link format: https://{site}.atlassian.net/jira/apps/{appId}/{envId}
 * App ID alone 404s — env ID is installation-specific. User pastes the working
 * URL once from the browser address bar while Timesheets is open.
 */
export const TIMESHEETS_FORGE_APP_ID = '879667b0-a01c-4427-a177-e923b24dc675';

/** Default deep link for m2c-innovis (appId + envId). Override via localStorage paste. */
export const DEFAULT_TIMESHEETS_APP_URL =
  'https://m2c-innovis.atlassian.net/jira/apps/879667b0-a01c-4427-a177-e923b24dc675/00950fc7-1b59-42b6-9437-1331b16ba960';

const STORAGE_PREFIX = 'complic_timesheet_submitted_';
const URL_STORAGE_KEY = 'complic_timesheets_app_url';

const MONTH_NAMES_CS = [
  'leden',
  'únor',
  'březen',
  'duben',
  'květen',
  'červen',
  'červenec',
  'srpen',
  'září',
  'říjen',
  'listopad',
  'prosinec',
];

export type TimesheetSubmitPeriod = {
  year: number;
  month: number; // 1–12
  key: string; // YYYY-MM
  label: string; // e.g. "říjen 2026"
};

export function getPreviousMonthPeriod(now = new Date()): TimesheetSubmitPeriod {
  const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const year = d.getFullYear();
  const month = d.getMonth() + 1;
  const key = `${year}-${String(month).padStart(2, '0')}`;
  return {
    year,
    month,
    key,
    label: `${MONTH_NAMES_CS[month - 1]} ${year}`,
  };
}

function storageKey(periodKey: string): string {
  return `${STORAGE_PREFIX}${periodKey}`;
}

export function isTimesheetPeriodSubmitted(periodKey: string): boolean {
  try {
    return localStorage.getItem(storageKey(periodKey)) === '1';
  } catch {
    return false;
  }
}

export function markTimesheetPeriodSubmitted(periodKey: string): void {
  try {
    localStorage.setItem(storageKey(periodKey), '1');
  } catch {
    /* ignore */
  }
}

export function clearTimesheetPeriodSubmitted(periodKey: string): void {
  try {
    localStorage.removeItem(storageKey(periodKey));
  } catch {
    /* ignore */
  }
}

/** Ready = previous month exists and not yet marked submitted in Complic. */
export function getTimesheetSubmitReady(now = new Date()): TimesheetSubmitPeriod | null {
  const period = getPreviousMonthPeriod(now);
  if (isTimesheetPeriodSubmitted(period.key)) return null;
  return period;
}

/** Normalize a pasted Timesheets URL to the stable /jira/apps/{appId}/{envId} base. */
export function normalizeTimesheetsAppUrl(raw: string): string | null {
  const trimmed = (raw || '').trim();
  if (!trimmed) return null;
  try {
    const u = new URL(trimmed);
    if (!u.hostname.endsWith('atlassian.net') && !u.hostname.endsWith('atlassian.com')) {
      return null;
    }
    // /jira/apps/{appId}/{envId}/...
    const m = u.pathname.match(
      /\/jira\/apps\/([0-9a-f-]{36})\/([0-9a-f-]{36})/i
    );
    if (m) {
      return `${u.origin}/jira/apps/${m[1]}/${m[2]}`;
    }
    // Accept full path if it at least contains the Timesheets app id
    if (u.pathname.includes(TIMESHEETS_FORGE_APP_ID)) {
      return `${u.origin}${u.pathname.replace(/\/$/, '')}`;
    }
    return null;
  } catch {
    return null;
  }
}

export function getSavedTimesheetsAppUrl(): string | null {
  try {
    const saved = localStorage.getItem(URL_STORAGE_KEY);
    if (!saved) return null;
    return normalizeTimesheetsAppUrl(saved) || saved;
  } catch {
    return null;
  }
}

export function saveTimesheetsAppUrl(url: string): string | null {
  const normalized = normalizeTimesheetsAppUrl(url);
  if (!normalized) return null;
  try {
    localStorage.setItem(URL_STORAGE_KEY, normalized);
  } catch {
    /* ignore */
  }
  return normalized;
}

export function clearTimesheetsAppUrl(): void {
  try {
    localStorage.removeItem(URL_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Prefer user-saved deep link; else site default (appId + envId).
 * Bare /jira/apps/{appId} 404s on Jira Cloud — never fall back to that.
 */
export function buildTimesheetsAppUrl(
  _jiraSiteUrl?: string | null
): string | null {
  return getSavedTimesheetsAppUrl() || DEFAULT_TIMESHEETS_APP_URL;
}
